import { proxy, ref } from 'valtio';
import { devtools } from 'valtio/utils';

import { EnhancedGLTF } from '@cartwhl/3d/gltf/types';
import deleteCharacter from '@cartwhl/common/api/deleteCharacter';
import getCharacters, { Character } from '@cartwhl/common/api/getCharacters';
import postCharacter, { Markers } from '@cartwhl/common/api/postCharacter';
import {
  postCharacterImageGeneration,
  postPrepareCharacterImageGeneration,
  postCompleteCharacterImageGeneration,
} from '@cartwhl/common/api/postCharacterImageGeneration';
import postCharacterReferenceUploadLink from '@cartwhl/common/api/postCharacterReferenceUploadLink';
import postCharacterUploadLink from '@cartwhl/common/api/postCharacterUploadLink';
import { postCreateCharacterFromImage } from '@cartwhl/common/api/postCreateCharacterFromImage';
import postPredictJointMapping from '@cartwhl/common/api/postPredictJointMapping';
import postWsAndFetchCharacter from '@cartwhl/common/api/postWsAndFetchCharacter';
import putCharacter from '@cartwhl/common/api/putCharacter';
import { StudioModalName } from '@cartwhl/common/stores/navigationStore';
import { FACE_EXPRESSIONS, HAND_OPTIONS } from '@cartwhl/common/types';
import {
  TRACKED_FEATURES,
  trackFeatureFailed,
  trackFeatureStarted,
  trackFeatureStepCompleted,
} from '@cartwhl/tracking/featureTracking';
import handleGenericErrors from '@cartwhl/common/utils/handleGenericErrors';
import { FileUploadState } from '@cartwhl/ui/feature/FileUploader';

import { CHAR_GENERATION_PREFIX } from 'components/CharacterPanel/constants';
import {
  characterPickerStore,
  navigationStore,
  noticesStore,
  sceneSettingsStore,
  userStore,
} from 'stores';
import { uiStore } from 'stores';
import parseFilename from 'utils/parseFilename';
import uploadUtil from 'utils/uploadUtil';

import { webSocketStore } from './';

// Processing time constants
export const DEFAULT_UPLOAD_TIME = 180000;
export const MEDIUM_VERTEX_UPLOAD_TIME = 180000;
export const HIGH_VERTEX_UPLOAD_TIME = 240000;
export const REFERENCE_UPLOAD_TIME = 15000;
export const VERTEX_MEDIUM_COUNT_THRESHOLD = 180000;
export const VERTEX_HIGH_COUNT_THRESHOLD = 250000;
export const RETRY_EXTENSION = 240000;
export const AUTO_RETARGET_UPLOAD_TIME = 180000;

// Store upload utilities by ID to properly manage intervals
const uploadUtilInstances = new Map();

export const DEFAULT_MOTION_CHARACTER_ID = 'char-upload-Axel';
export const DEFAULT_POSE_CHARACTER_ID = 'char-upload-Axel';

type UploadStatus = 'UPLOADING' | 'PROCESSING' | 'SUCCESS' | 'ERROR';
let _imageGenTimer: ReturnType<typeof setInterval> | null = null;

type CharacterPickerStoreState = {
  characters?: Array<Character>;
  activeCharacter?: Character;
  activeCharacterID: string;
  handOptions: Array<string>;
  faceExpressions: Array<string>;
  generationInProgress: boolean;
  generationJobID: string | undefined;
  imageGenerationImages: string[];
  imageGenerationError: string | null;
  imageGenerationProgress: number;
  imageGenerationStartTime?: number;
  loading: {
    characterFetch: boolean;
    imageGeneration: boolean;
  };
  magnifiedImageIndex: number | null;
  promptText: string;
  selectedImageIndex: number | null;
  spinnerText: string;
  upload?: FileUploadState;
  referenceUpload?: FileUploadState;
};

const defaultValues: CharacterPickerStoreState = {
  characters: undefined,
  activeCharacter: undefined,
  activeCharacterID: DEFAULT_MOTION_CHARACTER_ID,
  handOptions: [
    // top row
    HAND_OPTIONS.RELAXED,
    HAND_OPTIONS.CURLED,
    HAND_OPTIONS.OPEN_LOOSE,
    HAND_OPTIONS.OPEN_TIGHT,

    // second row
    HAND_OPTIONS.FIST,
    HAND_OPTIONS.FIST_CLENCHED,
    HAND_OPTIONS.FIST_RELAXED,
    HAND_OPTIONS.POINTING,

    // third row
    HAND_OPTIONS.POINTING_COOL,
    HAND_OPTIONS.THUMBS_UP,
    HAND_OPTIONS.PEACE,
    HAND_OPTIONS.ROCK,

    // last row
    HAND_OPTIONS.LOVE_YOU,
    HAND_OPTIONS.GRIPPING,
    HAND_OPTIONS.SPLAY,
    HAND_OPTIONS.CLAW,
  ],
  faceExpressions: [
    FACE_EXPRESSIONS.DEFAULT,
    FACE_EXPRESSIONS.ANGRY,
    FACE_EXPRESSIONS.ANNOYED,
    FACE_EXPRESSIONS.BORED,
    FACE_EXPRESSIONS.CONFUSION,
    FACE_EXPRESSIONS.DELIGHTED_GRIN,
    FACE_EXPRESSIONS.DELIGHTED,
    FACE_EXPRESSIONS.EXCITED,
    FACE_EXPRESSIONS.GOOF,
    FACE_EXPRESSIONS.HAPPY_LOOK_LEFT,
    FACE_EXPRESSIONS.HAPPY_LOOK_RIGHT,
    FACE_EXPRESSIONS.LOVE,
    FACE_EXPRESSIONS.NERVOUS,
    FACE_EXPRESSIONS.NEUTRAL,
    FACE_EXPRESSIONS.PAIN,
    FACE_EXPRESSIONS.PLEASED,
    FACE_EXPRESSIONS.PUZZLED,
    FACE_EXPRESSIONS.RAGE,
    FACE_EXPRESSIONS.ROLL_LOOK_LEFT,
    FACE_EXPRESSIONS.ROLL_LOOK_RIGHT,
    FACE_EXPRESSIONS.SAD,
    FACE_EXPRESSIONS.SAD_EYES_CLOSED,
    FACE_EXPRESSIONS.SAD_LOOK_LEFT,
    FACE_EXPRESSIONS.SAD_LOOK_RIGHT,
    FACE_EXPRESSIONS.SQUEEZE,
    FACE_EXPRESSIONS.STRETCH,
    FACE_EXPRESSIONS.STUNNED,
    FACE_EXPRESSIONS.SURPRISE,
    FACE_EXPRESSIONS.SUSPICIOUS,
    FACE_EXPRESSIONS.THINKING_LOOK_LEFT,
    FACE_EXPRESSIONS.THINKING_LOOK_RIGHT,
    FACE_EXPRESSIONS.WINK,
  ],
  loading: {
    characterFetch: false,
    imageGeneration: false,
  },
  promptText: '',
  upload: undefined,
  referenceUpload: undefined,
  selectedImageIndex: null,
  magnifiedImageIndex: null,
  imageGenerationImages: [],
  imageGenerationError: null,
  imageGenerationProgress: 0,
  imageGenerationStartTime: undefined,
  generationInProgress: false,
  generationJobID: undefined,
  spinnerText: getGeneratedSpinnerTextByStatus('3D_CONVERT_QUEUED'),
};

export const state = proxy<CharacterPickerStoreState>(defaultValues);

if (process.env.NODE_ENV === 'development') {
  devtools(state, { name: 'CharacterPicker', enabled: true });
}

/// Image Generation Actions
export function isGenerationJob(jobID?: string): boolean {
  return typeof jobID === 'string' && jobID.startsWith(CHAR_GENERATION_PREFIX);
}

export function clearImageGeneration() {
  state.imageGenerationImages = [];
  state.imageGenerationError = null;
  state.imageGenerationProgress = 0;
  state.imageGenerationStartTime = undefined;
}

/**
 * Creates a new character from an image
 * If the user uploaded a reference image then prompt should be the filename they originally uploaded.
 * @param selection
 */
export async function createCharacterFromImage(selection: {
  fileExtension: string;
  imageIndex: number;
  jobID: string;
  prompt: string;
}) {
  state.magnifiedImageIndex = selection.imageIndex;
  state.generationInProgress = true;

  try {
    await postCreateCharacterFromImage(selection);
    await addWebSocketsToCharacter(selection.jobID);
    state.upload = {
      uploadID: selection.jobID,
      status: 'QUEUED',
      createdAt: new Date().toISOString(),
      estimatedProcessingTime: 500000, // Just over 8 minutes
    };
  } catch (err: any) {
    state.imageGenerationError = err.message;
    state.generationInProgress = false;
  }
}

export function resetCharacterPickerState() {
  state.promptText = '';
  clearImageGeneration();
  state.loading.imageGeneration = false;
  state.selectedImageIndex = null;
  state.magnifiedImageIndex = null;
  state.generationInProgress = false;
  state.generationJobID = undefined;
  state.spinnerText = getGeneratedSpinnerTextByStatus('3D_CONVERT_QUEUED');
  state.referenceUpload = undefined;
}

export function setGenerationInProgress(isInProgress: boolean) {
  state.generationInProgress = isInProgress;
}

export function setImageGenerationLoading(isLoading: boolean) {
  state.loading.imageGeneration = isLoading;
  if (isLoading) {
    startImageGenerationTimer();
  } else {
    stopImageGenerationTimer();
  }
}

export function setImageGenerationProgress(progress: number) {
  state.imageGenerationProgress = progress;
}

export function startImageGenerationTimer() {
  if (_imageGenTimer) clearInterval(_imageGenTimer);

  state.imageGenerationStartTime = Date.now();
  state.imageGenerationProgress = 0;

  _imageGenTimer = setInterval(() => {
    if (!state.imageGenerationStartTime) return;

    const elapsed = Date.now() - state.imageGenerationStartTime;
    const pct = Math.min((elapsed / 45000) * 100, 100);
    state.imageGenerationProgress = pct;

    if (pct >= 100 && _imageGenTimer) {
      clearInterval(_imageGenTimer);
      _imageGenTimer = null;
    }
  }, 100);
}

export function stopImageGenerationTimer() {
  if (_imageGenTimer) {
    clearInterval(_imageGenTimer);
    _imageGenTimer = null;
  }
  state.imageGenerationStartTime = undefined;
}

export async function generateImagesFromPrompt(prompt: string) {
  clearImageGeneration();
  setImageGenerationLoading(true);

  try {
    // Step 1: Prepare — run Gemini descriptions, get jobID back fast
    const { data: prepData, error: prepError } =
      await postPrepareCharacterImageGeneration({ prompt });
    if (prepError) {
      handleGenericErrors(prepError);
      throw prepError;
    }
    const { jobID } = prepData!;

    // Step 2: Complete — generate images using the saved descriptions
    const { data, error } = await postCompleteCharacterImageGeneration({
      jobID,
      prompt,
    });
    if (error) {
      handleGenericErrors(error);
      throw error;
    }
    const { images } = data!;
    const urls = images.map((i) => i.imageURL);

    state.imageGenerationImages = urls;
    state.selectedImageIndex = urls.length > 0 ? 0 : null;
    state.magnifiedImageIndex = null;
    state.generationJobID = jobID;
    state.generationInProgress = false;
  } catch (err: any) {
    state.imageGenerationError = err.message || 'Failed to generate images.';
  } finally {
    state.loading.imageGeneration = false;
  }
}

export async function generateImagesFromReferenceImage(
  filename: string,
  fileExtension: string,
  jobID: string,
) {
  clearImageGeneration();
  setImageGenerationLoading(true);

  try {
    // Step 1: Prepare — run Gemini descriptions on the reference image
    const { data: prepData, error: prepError } =
      await postPrepareCharacterImageGeneration({ jobID, fileExtension });
    if (prepError) {
      handleGenericErrors(prepError);
      throw prepError;
    }

    // Step 2: Complete — generate images using the saved descriptions
    const { data, error } = await postCompleteCharacterImageGeneration({
      jobID,
      filename,
    });
    if (error) {
      handleGenericErrors(error);
      throw error;
    } else if (data) {
      const images = data?.images;
      const urls = images.map((i) => i.imageURL);

      state.imageGenerationImages = urls;
      state.selectedImageIndex = urls.length > 0 ? 0 : null;
      state.magnifiedImageIndex = null;
      state.generationJobID = jobID;
      state.generationInProgress = false;
    } else {
      throw new Error('Failed to generate images.');
    }
  } catch (err: any) {
    state.imageGenerationError = err.message || 'Failed to generate images.';
  } finally {
    state.loading.imageGeneration = false;
  }
}

export function setImageGenerationError(error: string) {
  state.imageGenerationError = error;
}

export function setMagnifiedImageIndex(index: number | null) {
  state.magnifiedImageIndex = index;
}

export function setPromptText(newText: string) {
  state.promptText = newText;
}

export function setSelectedImageIndex(index: number | null) {
  state.selectedImageIndex = index;
}

// Update character object with extra properties
function transformCharactersArray(ary: Array<Character>) {
  for (const i of ary) {
    if (i.readyOn === undefined) {
      i.readyOn = null;
      i.meshData = ref<{ current: EnhancedGLTF | null }>({
        current: null,
      });
    }
  }
}

///// Actions:
export async function fetchAvailableCharacters(
  limit = 200,
  nextToken?: string,
  sortAscending?: boolean,
) {
  if (state.loading.characterFetch === true) return;

  state.loading.characterFetch = true;
  const { data, error } = await getCharacters(limit, nextToken, sortAscending);
  state.loading.characterFetch = false;

  if (error) {
    await handleGenericErrors(error, {
      redirectTo: 'studio',
      suppressNotices: true,
    });
  } else if (data) {
    console.log('fetchAvailableCharacters - now ready');
    state.characters = data.characters.sort((a, b) =>
      a.characterName.localeCompare(b.characterName),
    );

    // Update data with extra properties needed for client use
    transformCharactersArray(state.characters);

    // In legacy motion mode (aka no project) update the active character after fetching all characters
    const { projectID } = navigationStore.getRouteParams();
    if (!projectID) {
      updateActiveCharacter();
    }

    const uploadingCharacter = data.characters.find(
      (c) => c.uploadStatus === 'PENDING',
    );

    // @mitch: How do we recover from refresh? What happens if we get stuck in auto-regging? Is there a way to cancel?
    const autoRiggingCharacter = data.characters.find(
      (c) => c.uploadStatus === ('AUTORIGGING_IN_PROGRESS' as any),
    );

    const retargetingCharacter = data.characters.find(
      (c) => c.uploadStatus === ('RETARGETING_IN_PROGRESS' as any),
    );

    // Recover upload or autoRigging in progress after page refresh
    if (uploadingCharacter && !state.upload) {
      // Recover an upload in progress
      let estimatedProcessingTime =
        uploadingCharacter.vertexCount > VERTEX_HIGH_COUNT_THRESHOLD
          ? HIGH_VERTEX_UPLOAD_TIME
          : uploadingCharacter.vertexCount > VERTEX_MEDIUM_COUNT_THRESHOLD
            ? MEDIUM_VERTEX_UPLOAD_TIME
            : DEFAULT_UPLOAD_TIME;
      let warningMessage =
        uploadingCharacter.vertexCount > VERTEX_MEDIUM_COUNT_THRESHOLD
          ? 'Your character is more complicated than the average character. It may take longer than normal to upload.'
          : undefined;
      if (
        uploadingCharacter.retriesRemaining !== undefined &&
        uploadingCharacter.retriesRemaining < 3
      ) {
        const numRetries = Math.min(3, 3 - uploadingCharacter.retriesRemaining);
        estimatedProcessingTime += numRetries * RETRY_EXTENSION;
        warningMessage =
          'Your upload timed out while processing and is being automatically retried. Please wait several minutes.';
      }
      state.upload = {
        uploadID: uploadingCharacter.jobID,
        status: 'PROCESSING',
        createdAt: uploadingCharacter.createdAt,
        estimatedProcessingTime,
        warningMessage: warningMessage,
      };

      // Create and store the upload utility instance
      const uploadUtilInstance = uploadUtil(state.upload, pollCharacterStatus);
      uploadUtilInstances.set(uploadingCharacter.jobID, uploadUtilInstance);

      // Start with the polling backup function
      uploadUtilInstance.startProcessingCounter();

      if (characterPickerStore.isGenerationJob(uploadingCharacter.jobID)) {
        state.generationInProgress = true;
        state.generationJobID = uploadingCharacter.jobID;
        state.imageGenerationImages = uploadingCharacter.referenceImagePath
          ? [uploadingCharacter.referenceImagePath]
          : [];
        state.selectedImageIndex = 0;
        state.magnifiedImageIndex = 0;
        const currentGenerationStatus = uploadingCharacter.generatedStatus;
        state.spinnerText = getGeneratedSpinnerTextByStatus(
          currentGenerationStatus,
        );
      }

      const results = await addWebSocketsToCharacter(uploadingCharacter.jobID);
      if (results && results.data) {
        // update the state with the results and update the uploadStatus as necessary
        state.characters = state.characters?.map((char) =>
          char.jobID === results.data!.jobID ? results.data! : char,
        );
        let newUploadStatus: UploadStatus = 'PROCESSING';
        if (results.data.uploadStatus === 'COMPLETE') {
          newUploadStatus = 'SUCCESS';
        } else if (results.data.uploadStatus === 'FAILED') {
          newUploadStatus = 'ERROR';
        }
        state.upload.status = newUploadStatus;
      }
    } else if (autoRiggingCharacter) {
      // Recover an autoRigging in progress
      await addWebSocketsToCharacter(autoRiggingCharacter.jobID);

      navigationStore.setModal(StudioModalName.AUTO_RIGGING, {
        characterID: autoRiggingCharacter.jobID,
        showStep: 2,
        autoRiggingInProgress: true,
      });
    } else if (retargetingCharacter) {
      // Recover a retargeting in progress
      await addWebSocketsToCharacter(retargetingCharacter.jobID);

      navigationStore.setModal(StudioModalName.RETARGETING, {
        characterID: retargetingCharacter.jobID,
        showStep: 2,
        retargetingInProgress: true,
      });
    }
  }
}

export function setUploadStatus(newStatus: UploadStatus) {
  if (state.upload) {
    state.upload.status = newStatus;

    // Clean up the upload utility instance when the upload completes
    if (newStatus === 'SUCCESS' || newStatus === 'ERROR') {
      uploadUtilInstances.delete(state.upload.uploadID);
    }
  } else {
    console.warn(
      'Trying to update upload state, but no active upload to be found',
    );
  }
}

/**
 * A polling backup function that checks if a character's upload has completed
 * This is used as a fallback when websocket messages are not received
 * @param uploadID The ID of the upload/character to check
 */
export async function pollCharacterStatus(uploadID: string): Promise<void> {
  console.log(`Polling character status for ${uploadID} as websocket backup`);

  try {
    // Fetch all characters to check the status
    const { data, error } = await getCharacters(10);

    if (error) {
      console.error('Error polling for character status:', error);
      return;
    }

    if (!data || !data.characters || !data.characters.length) {
      return;
    }

    // Find the character with matching jobID
    const character = data.characters.find((c) => c.jobID === uploadID);

    if (!character) {
      console.warn(`Character with ID ${uploadID} not found while polling`);
      return;
    }

    // Check if the character is no longer pending
    if (character.uploadStatus !== 'PENDING') {
      console.log(
        `Character ${uploadID} status changed to ${character.uploadStatus} via polling`,
      );

      // Update the characters list with the latest data
      state.characters = data.characters.sort((a, b) =>
        a.characterName.localeCompare(b.characterName),
      );

      // Simulate the same behavior as if we received a websocket message
      if (character.uploadStatus === 'COMPLETE') {
        uploadReady(uploadID);
      } else if (character.uploadStatus === 'FAILED') {
        // Handle failed uploads similar to the UPLOAD_FAILED websocket handler
        // Use a generic error message since Character type doesn't have errorMessages property
        setUploadError(['Upload failed. Please try again.']);
      }
    }
  } catch (error) {
    console.error('Error in polling character status:', error);
  }
}

export function setUploadError(reasons?: string[]) {
  setUploadStatus('ERROR');
  if (state.upload && reasons) {
    state.upload.errorReasons = reasons;
  }
}

export function removeUpload() {
  if (state.upload) {
    if (uploadUtilInstances.has(state.upload.uploadID)) {
      uploadUtilInstances.get(state.upload.uploadID)?.stopProcessingCounter();
      uploadUtilInstances.delete(state.upload.uploadID);
    }
  }
  if (state.referenceUpload) {
    if (uploadUtilInstances.has(`ref_${state.referenceUpload.uploadID}`)) {
      uploadUtilInstances
        .get(`ref_${state.referenceUpload.uploadID}`)
        ?.stopProcessingCounter();
      uploadUtilInstances.delete(`ref_${state.referenceUpload.uploadID}`);
    }
  }
  state.upload = undefined;
  state.referenceUpload = undefined;
}

export async function addWebSocketsToCharacter(characterID: string) {
  const { connectionID, endpoint } = webSocketStore.webSocket;
  if (connectionID && endpoint) {
    return await postWsAndFetchCharacter(characterID, {
      wsConnectionID: connectionID,
      wsHttpEndpoint: endpoint,
    });
  }
}

export async function beginCharacterUpload(
  file: File,
  autoRig?: boolean,
  markers?: Markers,
) {
  const { name: characterName, extension: rawExtension } = parseFilename(
    file.name,
  );
  // MuJoCo robots are uploaded as a .zip bundle (the .xml + mesh assets); the
  // backend identifies them by fileExtension 'mjcf'.
  const fileExtension =
    rawExtension.toLowerCase() === 'zip' ? 'mjcf' : rawExtension;

  trackFeatureStarted(TRACKED_FEATURES.CHARACTER_UPLOAD, {
    metadata: { file_extension: fileExtension, auto_rig: Boolean(autoRig) },
  });

  state.upload = {
    uploadID: '',
    status: 'UPLOADING',
    createdAt: new Date().toISOString(),
    errorReasons: undefined,
    fileTransferPercent: 0,
    estimatedProcessingTime: DEFAULT_UPLOAD_TIME,
  };

  const { data, error } = await postCharacterUploadLink(
    characterName,
    fileExtension,
  );
  if (error) {
    trackFeatureFailed(TRACKED_FEATURES.CHARACTER_UPLOAD, {
      error_message: 'Failed to get upload link',
    });
    characterPickerStore.setUploadError();
    await handleGenericErrors(error);
  } else if (data) {
    state.upload.uploadID = data.jobID;

    // Create and store upload utility instance
    const uploadUtilInstance = uploadUtil(state.upload, pollCharacterStatus);
    uploadUtilInstances.set(data.jobID, uploadUtilInstance);

    await uploadUtilInstance.upload(data.characterFileUploadURL, file);

    if (state.upload) {
      trackFeatureStepCompleted(
        TRACKED_FEATURES.CHARACTER_UPLOAD,
        'file_transfer',
        { character_id: state.upload.uploadID },
      );
      setUploadStatus('PROCESSING');
      await createCharacter(
        state.upload.uploadID,
        characterName,
        fileExtension,
        Boolean(autoRig),
        markers,
      );
    }
  }
}

export async function createCharacter(
  jobID: string,
  characterName: string,
  fileExtension: string,
  autoRig: boolean,
  bodyMarkerPositions?: Markers,
) {
  const { data, error } = await postCharacter({
    characterName,
    jobID,
    autoRig,
    fileExtension,
    bodyMarkerPositions,
    wsConnectionID: webSocketStore.webSocket?.connectionID,
    wsHttpEndpoint: webSocketStore.webSocket?.endpoint,
  });
  if (error) {
    setUploadStatus('ERROR');
    await handleGenericErrors(error);
  }
}

export async function autoRigCharacter(
  characterID: string,
  bodyMarkerPositions: Markers,
) {
  const { error } = await putCharacter(characterID, {
    autoRig: true,
    bodyMarkerPositions,
    wsConnectionID: webSocketStore.webSocket?.connectionID,
    wsHttpEndpoint: webSocketStore.webSocket?.endpoint,
  });
  if (error) {
    await handleGenericErrors(error);
    navigationStore.closeModal();
  } else {
    fetchAvailableCharacters();
  }
}

export async function deleteCustomCharacter(
  characterID: string,
  skipNotice?: boolean,
) {
  const { error } = await deleteCharacter(characterID);
  if (error) {
    await handleGenericErrors(error);
  } else if (!skipNotice) {
    noticesStore.success('Character deleted!');
    sceneSettingsStore.resetCharacter();
  }
}

/**
 * Updates the active character based on the character ID
 * @param characterID The ID of the character to set as active
 */
export function setActiveCharacterID(characterID: string) {
  state.activeCharacterID = characterID;
  updateActiveCharacter();
}

/**
 * Updates the activeCharacter object based on the activeCharacterID
 */
function updateActiveCharacter() {
  if (!state.characters) return;

  const character = state.characters.find(
    (c) => c.jobID === state.activeCharacterID,
  );

  if (character) {
    state.activeCharacter = character;
  } else {
    // Fall back to Axel if the specified character doesn't exist
    const axel = state.characters.find(
      (c) => c.jobID === DEFAULT_MOTION_CHARACTER_ID,
    );
    if (axel) {
      state.activeCharacter = axel;
      state.activeCharacterID = DEFAULT_MOTION_CHARACTER_ID;
    }
  }
}

/**
 * Gets the currently active character
 * @returns The active character or undefined if not found
 */
export function getActiveCharacter() {
  return state.activeCharacter;
}

export async function renameCustomCharacter(
  characterID: string,
  characterName: string,
) {
  const { error } = await putCharacter(characterID, {
    characterName,
  });
  if (error) {
    await handleGenericErrors(error);
  } else {
    // Update the character name in the store
    const character = state.characters?.find((c) => c.jobID === characterID);
    if (character) {
      character.characterName = characterName;
    }

    noticesStore.success('Character rename successful!');
  }
}

export async function predictJointMapping(userBones: string[]) {
  const { data, error } = await postPredictJointMapping(userBones);
  if (error) {
    await handleGenericErrors(error);
    return undefined;
  }
  return data?.predictedMapping;
}

export async function uploadImageReference(file: File) {
  const { name: characterName, extension: fileExtension } = parseFilename(
    file.name,
  );

  state.referenceUpload = {
    uploadID: '',
    status: 'UPLOADING',
    createdAt: new Date().toISOString(),
    errorReasons: undefined,
    fileTransferPercent: 0,
    estimatedProcessingTime: REFERENCE_UPLOAD_TIME,
  };

  const { data, error } = await postCharacterReferenceUploadLink(
    characterName,
    fileExtension,
  );
  if (error) {
    state.referenceUpload.status = 'ERROR';
    await handleGenericErrors(error);
  } else if (data) {
    state.referenceUpload.uploadID = data.jobID;

    // Create and store upload utility instance for reference upload
    const uploadUtilInstance = uploadUtil(state.referenceUpload);
    uploadUtilInstances.set(`ref_${data.jobID}`, uploadUtilInstance);

    await uploadUtilInstance.upload(data.referenceImageUploadURL, file);

    if (state.referenceUpload) {
      state.referenceUpload.status = 'PROCESSING';
      // When upload is complete, generate images using the filename as prompt
      state.promptText = characterName;
      generateImagesFromReferenceImage(file.name, fileExtension, data.jobID);
    }
  }
}

export function getGeneratedSpinnerTextByStatus(
  status:
    | '3D_CONVERT_QUEUED'
    | '3D_CONVERT_STARTED'
    | '3D_CONVERT_COMPLETE'
    | undefined,
) {
  switch (status) {
    case '3D_CONVERT_QUEUED':
      return 'Your request is queued and will be processed shortly...';
    case '3D_CONVERT_STARTED':
      return 'Building your 3D character. This can take up to 10 minutes.';
    case '3D_CONVERT_COMPLETE':
      return 'Rigging your 3D character. This can take a few minutes.';
    default:
      return 'Generating your character...';
  }
}

export function uploadReady(uploadID: string) {
  setUploadStatus('SUCCESS');
  if (navigationStore.state.modal.type === StudioModalName.AUTO_RIGGING) {
    trackFeatureStepCompleted(
      TRACKED_FEATURES.CHARACTER_UPLOAD_RIGGING,
      'server_rigging',
      { character_id: uploadID },
    );
    navigationStore.setModal(StudioModalName.AUTO_RIGGING, {
      characterID: uploadID,
      showStep: 2,
      autoRiggingInProgress: false,
    });
  } else if (navigationStore.state.modal.type === StudioModalName.RETARGETING) {
    trackFeatureStepCompleted(
      TRACKED_FEATURES.CHARACTER_UPLOAD_RETARGETING,
      'server_retargeting',
      { character_id: uploadID },
    );
    navigationStore.setModal(StudioModalName.RETARGETING, {
      characterID: uploadID,
      showStep: 2,
      retargetingInProgress: false,
    });
  } else {
    if (!state.upload || state.upload.uploadID !== uploadID) {
      return;
    }
    trackFeatureStepCompleted(
      TRACKED_FEATURES.CHARACTER_UPLOAD,
      'server_processing',
      { character_id: uploadID },
    );
    // When Rigging modal is not open, it's a new upload and we need to show the verification modal
    navigationStore.setModal(StudioModalName.UPLOAD_VERIFICATION, {
      characterID: uploadID,
    });
    state.generationInProgress = false;
    resetCharacterPickerState();
    uiStore.setSubPanelKey('');
  }
}

//// WEBSOCKET EVENTS
webSocketStore.on('UPLOAD_COMPLETE', async (data) => {
  const uploadID = data.jobID;
  await fetchAvailableCharacters();
  uploadReady(uploadID);
  // Refetch userInfo to keep the amount of spins up to date
  userStore.refetchUserInfo();
});

webSocketStore.on('UPLOAD_FAILED', async (data) => {
  const errorMessage = data.errorMessages?.join('; ') ?? 'Unknown error';
  setUploadError(data.errorMessages);
  await fetchAvailableCharacters();

  if (data.errorMessages.some((e) => e.includes('No joint mapping found'))) {
    trackFeatureFailed(TRACKED_FEATURES.CHARACTER_UPLOAD, {
      character_id: data.jobID,
      error_message: 'No joint mapping found',
    });
    noticesStore.info(
      `No joint mapping found. Starting manual retargeting process`,
    );
    navigationStore.setModal(StudioModalName.RETARGETING, {
      characterID: data.jobID,
    });
  }

  if (navigationStore.state.modal.type === StudioModalName.AUTO_RIGGING) {
    trackFeatureFailed(TRACKED_FEATURES.CHARACTER_UPLOAD_RIGGING, {
      character_id: data.jobID,
      error_message: errorMessage,
    });
    noticesStore.error(
      `Auto-rigging failed. Please try again. ${data.errorMessages}`,
    );
    navigationStore.setModal(StudioModalName.AUTO_RIGGING, {
      characterID: data.jobID,
      showStep: 1,
    });
  }

  if (navigationStore.state.modal.type === StudioModalName.RETARGETING) {
    trackFeatureFailed(TRACKED_FEATURES.CHARACTER_UPLOAD_RETARGETING, {
      character_id: data.jobID,
      error_message: errorMessage,
    });
    noticesStore.error(
      `Retargeting failed. Please try again. ${data.errorMessages}`,
    );
    navigationStore.setModal(StudioModalName.RETARGETING, {
      characterID: data.jobID,
      showStep: 1,
    });
  }

  // Refetch userInfo to keep the amount of spins up to date
  userStore.refetchUserInfo();
});

webSocketStore.on('UPLOAD_WARNING', (data) => {
  if (state.upload && state.upload.uploadID === data.jobID) {
    state.upload.warningMessage = data.warningMessage;
    if (state.upload.estimatedProcessingTime) {
      const extendedTime = data.extendedProcessingTimeMs ?? 0;
      state.upload.estimatedProcessingTime += extendedTime;

      // Get the existing upload utility instance and restart the counter
      const uploadUtilInstance = uploadUtilInstances.get(data.jobID);
      if (uploadUtilInstance) {
        uploadUtilInstance.startProcessingCounter();
      }
    }
  }
});

webSocketStore.on('UPLOAD_NEEDS_VALIDATION', async (data) => {
  setUploadStatus('SUCCESS');
  await fetchAvailableCharacters();

  noticesStore.error(`Auto-retargeting failed. Proceed to manual retargeting.`);
  navigationStore.setModal(StudioModalName.RETARGETING, {
    characterID: data.jobID,
    showStep: 0,
  });
});

webSocketStore.on('3D_CONVERT_STARTED', (data) => {
  state.spinnerText = getGeneratedSpinnerTextByStatus('3D_CONVERT_STARTED');
  if (state.upload) {
    state.upload.status = 'PROCESSING';
    uploadUtil(state.upload, pollCharacterStatus).startProcessingCounter();
  }
});

webSocketStore.on('3D_CONVERT_COMPLETE', (data) => {
  state.spinnerText = getGeneratedSpinnerTextByStatus('3D_CONVERT_COMPLETE');
});
